import { useCallback } from 'react';
import { create } from 'zustand';
import { activityTracker } from '../utils/activityTracker';
import { LANGUAGE_OPTIONS } from '../MainMenu';
import {
  getInitialWelcomeScreenState,
  getStartupLocationFromUrlOrStorage,
  StartupLocation,
} from './controller';

export type DerivedLocationTopicMeta = {
  topicName: string;
  adminLevel: string;
  zoom: number;
  zoomBucket: string;
  priority: string[];
};

export type AppStateFields = {
  displayLocation: any;
  mapZoom: number | null;
  articles: any[];
  selectedArticle: any;
  focusedTopSightMarkerId: string | null;
  viewportWikiLanguage: string;
  userLanguage: string;
  userLanguageLabel: string;
  isMenuOpen: boolean;
  showSalesPrompt: boolean;
  showQuotaModal: boolean;
  showIOSInstallManual: boolean;
  showAndroidInstallManual: boolean;
  showSuccessNotification: boolean;
  showSigninSuccessBanner: boolean;
  showUserHistory: boolean;
  isMapCenteredOnUser: boolean;
  autoShowLocationSights: boolean;
  topSights: any[];
  articleIcons: Record<string, string>;
  topSightIcons: Record<string, string>;
  displayedArticles: Set<string | number>;
  history: any[];
  historyIndex: number;
  mapTypeId: string;
  detailsCardHeightPx: number;
  isDesktopUI: boolean;
  isFromSearchfield: boolean;
  searchFieldLocationString: string | null;
  searchFieldRequestRequired: boolean;
  showWelcomeScreen: boolean;
  boundsSnapshot: any;
  lastGeocodingCall: number;
  initialLocationSet: boolean;
  initialNominatimTopicSet: boolean;
  selectedStartupLocation: StartupLocation;
  initialMapConfigured: boolean;
  showTooltip: boolean;
  lastZoomLevel: number | null;
  thumbResolution: number;
};

type ReducerKey = keyof AppStateFields;

export type AppAction =
  | { type: 'setField'; key: ReducerKey; value: AppStateFields[ReducerKey] }
  | { type: 'patch'; patch: Partial<AppStateFields> }
  | { type: 'setDisplayLocation'; value: any }
  | { type: 'setMapZoom'; value: number | null }
  | { type: 'setUserLanguage'; value: string }
  | { type: 'setTopSights'; value: any[] }
  | { type: 'pushHistory'; entry: any }
  | { type: 'replaceHistory'; history: any[]; historyIndex: number }
  | { type: 'resetSearchFieldTracking' }
  | { type: 'reconcileFocusedTopSight'; ids: string[] };

export type AppStoreState = AppStateFields & {
  dispatch: (action: AppAction) => void;
  setShowSalesPrompt: (value: boolean) => void;
  setShowQuotaModal: (value: boolean) => void;
  setShowSuccessNotification: (value: boolean) => void;
  setShowSigninSuccessBanner: (value: boolean) => void;
};

const TOPIC_PRIORITY_LOW_ZOOM = ['country', 'state', 'city', 'town', 'village', 'hamlet', 'county'];
const TOPIC_PRIORITY_MID_ZOOM = ['city', 'town', 'village', 'county', 'state', 'country', 'hamlet'];
const TOPIC_PRIORITY_HIGH_ZOOM = ['city', 'town', 'village', 'hamlet', 'county', 'state', 'country'];

function clean(value: any) {
  if (value == null) return '';
  return String(value).trim();
}

function pickFirstAddressField(address: Record<string, any> = {}, keys: string[] = []) {
  for (const key of keys) {
    const value = clean(address?.[key]);
    if (value) {
      return { key, value };
    }
  }
  return { key: '', value: '' };
}

export function deriveTopicFromAddressByZoom(address: Record<string, any> = {}, currentZoom = 10): DerivedLocationTopicMeta {
  const zoom = Number.isFinite(Number(currentZoom)) ? Number(currentZoom) : 10;

  let priority: string[] = TOPIC_PRIORITY_HIGH_ZOOM;
  let zoomBucket = '';

  if (zoom < 6) {
    zoomBucket = '<6';
    priority = TOPIC_PRIORITY_LOW_ZOOM;
  } else if (zoom >= 6 && zoom <= 7) {
    zoomBucket = '6-7';
    priority = TOPIC_PRIORITY_MID_ZOOM;
  } else {
    zoomBucket = '>7';
    priority = TOPIC_PRIORITY_HIGH_ZOOM;
  }

  const selected = pickFirstAddressField(address, priority);
  return {
    topicName: selected.value || '',
    adminLevel: selected.key || '',
    zoom,
    zoomBucket,
    priority,
  };
}

export function attachDerivedTopicSelection(locationData: any, currentZoom = 10) {
  if (!locationData) return locationData;

  const topic = deriveTopicFromAddressByZoom(locationData?.address || {}, currentZoom);

  return {
    ...locationData,
    topic_place_name: topic.topicName || locationData?.topic_place_name || '',
    topic_admin_level: topic.adminLevel || locationData?.topic_admin_level || '',
  };
}

function getDefaultUserLanguage() {
  if (typeof window === 'undefined') return 'en-US';

  try {
    const savedLanguage = localStorage.getItem('userLanguage');
    if (savedLanguage) {
      const match = LANGUAGE_OPTIONS.find((opt) => opt.code === savedLanguage);
      if (match) return savedLanguage;
    }
  } catch {}

  const browserLanguages = navigator.languages || [navigator.language];
  for (const lang of browserLanguages) {
    const match = LANGUAGE_OPTIONS.find((opt) => opt.code.toLowerCase() === String(lang).toLowerCase());
    if (match) return match.code;
  }

  return 'en-US';
}

export function getUserLanguageLabel(code: string) {
  return LANGUAGE_OPTIONS.find((opt) => opt.code === code)?.label || 'English';
}

function getInitialQuotaModalState() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('quota') === '1' || params.get('quota') === 'true';
}

function getInitialDesktopUiState() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches;
}

export function createInitialAppState(): AppStateFields {
  const userLanguage = getDefaultUserLanguage();

  return {
    displayLocation: null,
    mapZoom: null,
    articles: [],
    selectedArticle: null,
    focusedTopSightMarkerId: null,
    viewportWikiLanguage: 'en',
    userLanguage,
    userLanguageLabel: getUserLanguageLabel(userLanguage),
    isMenuOpen: false,
    showSalesPrompt: false,
    showQuotaModal: getInitialQuotaModalState(),
    showIOSInstallManual: false,
    showAndroidInstallManual: false,
    showSuccessNotification: false,
    showSigninSuccessBanner: false,
    showUserHistory: false,
    isMapCenteredOnUser: false,
    autoShowLocationSights: false,
    topSights: [],
    articleIcons: {},
    topSightIcons: {},
    displayedArticles: new Set(),
    history: [],
    historyIndex: -1,
    mapTypeId: 'roadmap',
    detailsCardHeightPx: 0,
    isDesktopUI: getInitialDesktopUiState(),
    isFromSearchfield: false,
    searchFieldLocationString: null,
    searchFieldRequestRequired: false,
    showWelcomeScreen: getInitialWelcomeScreenState(),
    boundsSnapshot: null,
    lastGeocodingCall: 0,
    initialLocationSet: false,
    initialNominatimTopicSet: false,
    selectedStartupLocation: getStartupLocationFromUrlOrStorage(),
    initialMapConfigured: false,
    showTooltip: false,
    lastZoomLevel: null,
    thumbResolution: 180,
  };
}

export function appReducer(state: AppStateFields, action: AppAction): AppStateFields {
  switch (action.type) {
    case 'setField':
      return {
        ...state,
        [action.key]: action.value,
      };
    case 'patch':
      return {
        ...state,
        ...action.patch,
      };
    case 'setDisplayLocation':
      return {
        ...state,
        displayLocation: action.value,
      };
    case 'setMapZoom':
      return {
        ...state,
        mapZoom: action.value,
      };
    case 'setUserLanguage':
      return {
        ...state,
        userLanguage: action.value,
        userLanguageLabel: getUserLanguageLabel(action.value),
      };
    case 'setTopSights': {
      const nextTopSights = Array.isArray(action.value) ? action.value : [];
      const ids = nextTopSights
        .map((item) => String(item?.id || item?.link || '').trim())
        .filter(Boolean);
      const shouldClearFocus = !!state.focusedTopSightMarkerId && !ids.includes(state.focusedTopSightMarkerId);
      return {
        ...state,
        topSights: nextTopSights,
        focusedTopSightMarkerId: shouldClearFocus ? null : state.focusedTopSightMarkerId,
      };
    }
    case 'pushHistory': {
      const nextHistory = state.history.slice(0, state.historyIndex + 1).concat(action.entry);
      return {
        ...state,
        history: nextHistory,
        historyIndex: nextHistory.length - 1,
      };
    }
    case 'replaceHistory':
      return {
        ...state,
        history: action.history,
        historyIndex: action.historyIndex,
      };
    case 'resetSearchFieldTracking':
      return {
        ...state,
        isFromSearchfield: false,
        searchFieldLocationString: null,
        searchFieldRequestRequired: false,
      };
    case 'reconcileFocusedTopSight':
      if (!state.focusedTopSightMarkerId) return state;
      if (action.ids.includes(state.focusedTopSightMarkerId)) return state;
      return {
        ...state,
        focusedTopSightMarkerId: null,
      };
    default:
      return state;
  }
}

export const selectDisplayLocation = (state: AppStoreState) => state.displayLocation;
export const selectMapZoom = (state: AppStoreState) => state.mapZoom ?? 10;
export const selectDerivedLocationTopicMeta = (state: AppStoreState) =>
  deriveTopicFromAddressByZoom(state.displayLocation?.address || {}, state.mapZoom ?? 10);
export const selectDerivedLocationName = (state: AppStoreState) =>
  selectDerivedLocationTopicMeta(state).topicName || '';

export const useAppStore = create<AppStoreState>((set) => ({
  ...createInitialAppState(),
  dispatch: (action) => {
    set((current) => appReducer(current, action));
  },
  setShowSalesPrompt: (value) => set({ showSalesPrompt: value }),
  setShowQuotaModal: (value) => set({ showQuotaModal: value }),
  setShowSuccessNotification: (value) => set({ showSuccessNotification: value }),
  setShowSigninSuccessBanner: (value) => set({ showSigninSuccessBanner: value }),
}));

export function setAppField<K extends ReducerKey>(key: K, value: AppStateFields[K]) {
  useAppStore.getState().dispatch({
    type: 'setField',
    key,
    value,
  });
}


export function patchAppState(patch: Partial<AppStateFields>) {
  useAppStore.getState().dispatch({
    type: 'patch',
    patch,
  });
}

export function setUserLanguageCode(value: string) {
  useAppStore.getState().dispatch({
    type: 'setUserLanguage',
    value,
  });
}

export function setTopSightsList(value: any[]) {
  useAppStore.getState().dispatch({
    type: 'setTopSights',
    value,
  });
}

export function useAppField<K extends ReducerKey>(
  key: K,
): [AppStateFields[K], (value: AppStateFields[K] | ((previous: AppStateFields[K]) => AppStateFields[K])) => void] {
  const value = useAppStore((state) => state[key]);
  const dispatch = useAppStore((state) => state.dispatch);

  const setValue = useCallback((nextValue: AppStateFields[K] | ((previous: AppStateFields[K]) => AppStateFields[K])) => {
    const previous = useAppStore.getState()[key] as AppStateFields[K];
    const resolved = typeof nextValue === 'function'
      ? (nextValue as (previous: AppStateFields[K]) => AppStateFields[K])(previous)
      : nextValue;
    dispatch({ type: 'setField', key, value: resolved });
  }, [dispatch, key]);

  return [value, setValue];
}

export function dismissWelcomeScreen() {
  activityTracker.updateLastActivity();
  useAppStore.getState().dispatch({
    type: 'setField',
    key: 'showWelcomeScreen',
    value: false,
  });
}
